import React, { useState, useEffect, useCallback } from 'react';
import { AdventureInvite, UserId, AdventureInviteStatus } from 'coop-adventure-engine';
import adventureInvitesService from '../../services/adventureInvitesService';
import { CharacterPortrait } from './CharacterPortrait';
import apiService from '../../services/api';
import useGameStore from '../../stores/gameStore';

export interface AdventureInviteStatusListProps {
  /**
   * Current session ID (optional, will use current session from game store if not provided)
   */
  sessionId?: string;

  /**
   * Optional click handler for adventure invites
   * If provided, invites will be clickable
   */
  onInviteClick?: (inviteId: string) => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom styles
   */
  style?: React.CSSProperties;

  /**
   * Optional custom button color for clickable invite items (e.g. from adventure button_color)
   */
  customColor?: string;
}

/**
 * Component for displaying list of sent adventure invites with status
 * Shows invitee information (name and portrait) and status badges
 * Filters invites by current session ID
 */
const getColorWithAlpha = (color: string, alpha: string) => {
  if (color.startsWith('#')) {
    return `${color}${alpha}`;
  }
  return color;
};

export const AdventureInviteStatusList: React.FC<AdventureInviteStatusListProps> = ({
  sessionId,
  onInviteClick,
  className = '',
  style,
  customColor
}) => {
  const { currentSession } = useGameStore();
  const [invites, setInvites] = useState<AdventureInvite[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Cache for invitee info (name and portrait)
  const [userInfoCache, setUserInfoCache] = useState<Map<UserId, { name: string; portraitId?: string }>>(new Map());

  // Use provided sessionId or fall back to current session
  const activeSessionId = sessionId || currentSession?.id;

  const loadSentInvites = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const allSentInvites = await adventureInvitesService.getSentAdventureInvites();
      
      // Filter by current session ID if available
      const filteredInvites = activeSessionId
        ? allSentInvites.filter(inv => inv.sessionId === activeSessionId)
        : allSentInvites;
      
      setInvites(filteredInvites);
      
      // Load user info for all invitees that we don't have cached
      setUserInfoCache((prevCache) => {
        const newCache = new Map(prevCache);
        const invitesToLoad = filteredInvites.filter(inv => !newCache.has(inv.inviteeId));
        
        // Load user info in parallel
        Promise.all(
          invitesToLoad.map(async (invite) => {
            try {
              const userInfo = await apiService.getUserInfo(invite.inviteeId);
              setUserInfoCache((currentCache) => {
                const updatedCache = new Map(currentCache);
                const cacheEntry: { name: string; portraitId?: string } = {
                  name: userInfo.playerName || userInfo.username || `User ${invite.inviteeId.substring(0, 8)}`
                };
                if (userInfo.portraitId) {
                  cacheEntry.portraitId = userInfo.portraitId;
                }
                updatedCache.set(invite.inviteeId, cacheEntry);
                return updatedCache;
              });
            } catch (err) {
              console.error(`Failed to load user info for ${invite.inviteeId}:`, err);
              setUserInfoCache((currentCache) => {
                const updatedCache = new Map(currentCache);
                updatedCache.set(invite.inviteeId, {
                  name: `User ${invite.inviteeId.substring(0, 8)}`
                });
                return updatedCache;
              });
            }
          })
        );
        
        return newCache;
      });
    } catch (err) {
      console.error('Failed to load sent adventure invites:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sent adventure invites');
    } finally {
      setIsLoading(false);
    }
  }, [activeSessionId]);

  useEffect(() => {
    loadSentInvites();

    // Listen for adventure invite updates
    const handleAdventureInviteUpdate = () => {
      loadSentInvites();
    };

    window.addEventListener('adventure_invite:update', handleAdventureInviteUpdate);
    return () => {
      window.removeEventListener('adventure_invite:update', handleAdventureInviteUpdate);
    };
  }, [loadSentInvites]);

  const getStatusTextColor = (status: AdventureInviteStatus): string => {
    switch (status) {
      case 'pending':
        return 'text-yellow-300';
      case 'accepted':
        return 'text-green-300';
      case 'rejected':
        return 'text-red-300';
      case 'cancelled':
        return 'text-gray-300';
      case 'expired':
        return 'text-orange-300';
      default:
        return 'text-gray-300';
    }
  };

  const getStatusLabel = (status: AdventureInviteStatus): string => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'accepted':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
        return 'Cancelled';
      case 'expired':
        return 'Expired';
      default:
        return status;
    }
  };

  const handleInviteClick = useCallback(
    (inviteId: string) => {
      if (onInviteClick) {
        onInviteClick(inviteId);
      }
    },
    [onInviteClick]
  );

  if (isLoading) {
    return (
      <div
        className={`adventure-invite-status-list loading ${className}`}
        style={style}
        data-testid="adventure-invite-status-list-loading"
      >
        <div className="text-center py-4">
          <p className="text-gray-400 italic text-sm">Loading invite status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`adventure-invite-status-list error ${className}`}
        style={style}
        data-testid="adventure-invite-status-list-error"
      >
        <div className="text-center py-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (invites.length === 0) {
    return (
      <div
        className={`adventure-invite-status-list empty ${className}`}
        style={style}
        data-testid="adventure-invite-status-list-empty"
      >
        <div className="flex items-center justify-center py-2" data-testid="empty-adventure-invite-status">
          <p className="italic text-center text-gray-400">No sent invites for this session</p>
        </div>
      </div>
    );
  }

  // If onInviteClick is provided, render as clickable buttons
  if (onInviteClick) {
    return (
      <div
        className={`adventure-invite-status-list selectable ${className}`}
        style={style}
        data-testid="adventure-invite-status-list-selectable"
      >
        <div
          className="adventure-invite-status-list-items flex flex-col"
          style={{ gap: '0.5rem' }}
          data-testid="adventure-invite-status-items-list"
        >
          {invites.map((invite) => {
            const userInfo = userInfoCache.get(invite.inviteeId);
            const displayName = userInfo?.name || `User ${invite.inviteeId.substring(0, 8)}`;
            const portraitId = userInfo?.portraitId;
            // Check if status is 'rejected' - AdventureInviteStatus is a string literal type: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled'
            const isRejected = invite.status === 'rejected';
            
            // Debug: Log rejected invites to verify status value
            if (invite.status === 'rejected') {
              console.log('[AdventureInviteStatusList] Rendering rejected invite:', { 
                inviteId: invite.id, 
                status: invite.status, 
                statusType: typeof invite.status,
                displayName 
              });
            }
            
            const buttonStyle: React.CSSProperties = {};
            if (customColor) {
              buttonStyle.border = `1px solid ${getColorWithAlpha(customColor, '40')}`;
              buttonStyle.background = `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`;
            }
            
            return (
              <button
                key={invite.id}
                onClick={() => handleInviteClick(invite.id)}
                className="adventure-invite-status-item-button inventory-item-button w-full"
                style={buttonStyle}
                data-testid={`adventure-invite-status-${invite.id}`}
                data-invite-id={invite.id}
                data-invite-status={invite.status}
                aria-label={`Select invite for ${displayName}`}
              >
                <div className="flex items-center w-full" style={{ padding: '0.75rem 1rem' }}>
                  {/* Portrait on the left */}
                  <div className="flex-shrink-0" style={{ marginRight: '1rem' }}>
                    <CharacterPortrait
                      portraitId={portraitId}
                      variant="thumbnail"
                      alt={displayName}
                    />
                  </div>

                  {/* Name aligned to the left */}
                  <div className="flex-1 flex items-center">
                    <span 
                      className={isRejected ? '' : 'text-base'}
                      style={isRejected ? { color: '#ef4444', fontWeight: 'bold' } : {}}
                    >
                      {displayName}
                    </span>
                  </div>

                  {/* Status in italic on the right */}
                  <div className="flex-shrink-0">
                    <span 
                      className={isRejected ? '' : `text-sm italic ${getStatusTextColor(invite.status)}`}
                      style={isRejected ? { color: '#ef4444', fontWeight: 'bold', fontStyle: 'italic' } : {}}
                    >
                      {getStatusLabel(invite.status)}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Regular (non-selectable) invite status list - read-only display
  return (
    <div
      className={`adventure-invite-status-list ${className}`}
      style={style}
      data-testid="adventure-invite-status-list"
    >
      <div className="flex flex-col" style={{ gap: '0.5rem' }}>
        {invites.map((invite) => {
          const userInfo = userInfoCache.get(invite.inviteeId);
          const displayName = userInfo?.name || `User ${invite.inviteeId.substring(0, 8)}`;
          const portraitId = userInfo?.portraitId;
          // Check if status is 'rejected' - AdventureInviteStatus is a string literal type: 'pending' | 'accepted' | 'rejected' | 'expired' | 'cancelled'
          const isRejected = invite.status === 'rejected';
          
          // Debug: Log rejected invites to verify status value
          if (invite.status === 'rejected') {
            console.log('[AdventureInviteStatusList] Rendering rejected invite (non-selectable):', { 
              inviteId: invite.id, 
              status: invite.status, 
              statusType: typeof invite.status,
              displayName 
            });
          }
          
          return (
            <div
              key={invite.id}
              className="adventure-invite-status-card"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '0.75rem',
                padding: '0.75rem 1rem',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
              }}
              data-testid={`adventure-invite-status-card-${invite.id}`}
              data-invite-status={invite.status}
            >
              <div className="flex items-center w-full">
                {/* Portrait on the left */}
                <div className="flex-shrink-0" style={{ marginRight: '1rem' }}>
                  <CharacterPortrait
                    portraitId={portraitId}
                    variant="thumbnail"
                    alt={displayName}
                  />
                </div>

                {/* Name aligned to the left */}
                <div className="flex-1 flex items-center">
                  <span 
                    className={isRejected ? '' : 'text-base text-white'}
                    style={isRejected ? { color: '#ef4444', fontWeight: 'bold' } : {}}
                  >
                    {displayName}
                  </span>
                </div>

                {/* Status in italic on the right */}
                <div className="flex-shrink-0">
                  <span 
                    className={isRejected ? '' : `text-sm italic ${getStatusTextColor(invite.status)}`}
                    style={isRejected ? { color: '#ef4444', fontWeight: 'bold', fontStyle: 'italic' } : {}}
                  >
                    {getStatusLabel(invite.status)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdventureInviteStatusList;

